// src/utils/SeoHead.jsx
// Gắn theo từng route: <link rel="canonical">, hreflang (en / vi / x-default), <meta name="robots">,
// JSON-LD (Organization + WebSite + WebPage) và <html lang>.
// Không cần thư viện ngoài. Đặt <SeoHead /> BÊN TRONG <BrowserRouter>.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getLangFromPath } from "./LangCookie";

// ====== CẤU HÌNH CỦA SITE (chỉnh ở đây) ======
const SEO = {
  siteUrl: "https://gopaperless.mobile-id.vn", // domain chính thức, https, KHÔNG có "/" cuối
  siteName: "GoPaperless", // tên website (WebSite.name)
  orgName: "GoPaperless", // tên tổ chức (Organization.name) - đổi sang tên pháp nhân nếu muốn
  logo: "", // vd "/logo512.png" (đường dẫn tuyệt đối trên site). Để trống = bỏ qua
  sameAs: [], // vd ["https://www.linkedin.com/company/...", "https://www.facebook.com/..."]
  noindexPaths: [/^\/open-gopaperless-agent-in-desktop$/], // các route tiện ích không muốn Google index
};
const PUBLIC_URL = "";
// ==============================================

const LANGS = ["en", "vi"];
const DEFAULT_LANG = "en";
const DEFAULT_ROBOTS = "index, follow, max-image-preview:large";
const SEO_ATTR = "data-seo"; // đánh dấu các thẻ do component này tạo để dọn khi đổi route

const currentLang = () =>
  getLangFromPath(window.location.pathname) || DEFAULT_LANG;
// Không có dấu "/" cuối: trang chủ => "" nên URL là https://domain/en (khớp với redirect hiện tại)
const normalizePath = (p) => (p || "/").replace(/\/+$/, "");
const urlFor = (lang, path) => `${SEO.siteUrl}${PUBLIC_URL}/${lang}${path}`;
const robotsFor = (path) =>
  SEO.noindexPaths.some((re) => re.test(path || "/"))
    ? "noindex, follow"
    : DEFAULT_ROBOTS;

function addTag(tag, attrs) {
  const el = document.createElement(tag);
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  el.setAttribute(SEO_ATTR, "");
  document.head.appendChild(el);
  return el;
}

function setRobots(content) {
  let el = document.head.querySelector('meta[name="robots"]');
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", "robots");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function buildJsonLd(lang, path, canonical) {
  const orgId = `${SEO.siteUrl}/#organization`;
  const siteId = `${urlFor(lang, "")}#website`;

  const organization = {
    "@type": "Organization",
    "@id": orgId,
    name: SEO.orgName,
    url: SEO.siteUrl,
    ...(SEO.logo ? { logo: `${SEO.siteUrl}${SEO.logo}` } : {}),
    ...(SEO.sameAs.length ? { sameAs: SEO.sameAs } : {}),
  };
  const website = {
    "@type": "WebSite",
    "@id": siteId,
    url: urlFor(lang, ""),
    name: SEO.siteName,
    inLanguage: lang,
    publisher: { "@id": orgId },
  };
  const graph = [organization, website];

  if (path !== "") {
    graph.push({
      "@type": "WebPage",
      "@id": `${canonical}#webpage`,
      url: canonical,
      inLanguage: lang,
      isPartOf: { "@id": siteId },
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

export default function SeoHead() {
  const { pathname } = useLocation(); // đã bỏ basename (/en, /vi)

  useEffect(() => {
    const lang = currentLang();
    const path = normalizePath(pathname);
    const canonical = urlFor(lang, path);

    document.head
      .querySelectorAll(`[${SEO_ATTR}]`)
      .forEach((el) => el.remove());

    addTag("link", { rel: "canonical", href: canonical });
    LANGS.forEach((l) =>
      addTag("link", { rel: "alternate", hreflang: l, href: urlFor(l, path) }),
    );
    addTag("link", {
      rel: "alternate",
      hreflang: "x-default",
      href: urlFor(DEFAULT_LANG, path),
    });

    setRobots(robotsFor(path));
    document.documentElement.lang = lang;

    const ld = addTag("script", { type: "application/ld+json" });
    ld.text = JSON.stringify(buildJsonLd(lang, path, canonical));
  }, [pathname]);

  return null;
}

// Dùng cho route 404 (path="*"): báo Google không index trang "không tìm thấy" (tránh soft-404).
// Ví dụ: <Route path="*" element={<><NotFound /><NoIndex /></>} />
export function NoIndex() {
  useEffect(() => {
    setRobots("noindex, follow");
    return () => {
      // rời trang 404 => trả robots về theo route hiện tại
      const lang = currentLang();
      const rest = window.location.pathname.slice(
        `${PUBLIC_URL}/${lang}`.length,
      );
      setRobots(robotsFor(normalizePath(rest)));
    };
  }, []);
  return null;
}
